<template>
  <div :class="$style.testimonialsContainer">
    <TransitionOnScroll :translateY="30">
      <header :class="$style.header">
        <Heading :class="$style.title" :tag="HEADINGS.h2">{{ $t('title') }}</Heading>
        <p :class="$style.subtitle">{{ $t('subtitle') }}</p>
      </header>
    </TransitionOnScroll>

    <TransitionOnScroll :translateY="40">
      <HomeTestimonialsHighlights :items="highlightItems" />
    </TransitionOnScroll>

    <TransitionOnScroll :scale="0.98">
      <div :class="$style.masonryWrap">
        <!--
          Clip hardens overflow for iOS. Fade + CTAs stay outside the clip so
          Accordion’s will-change stacking cannot paint cards over the gradient
          (Safari hairline at the bottom edge).
        -->
        <div :class="$style.masonryClip">
          <Accordion
            ref="masonryAccordion"
            :isOpen="false"
            :collapsedHeight="masonryClipHeight"
            :isHeightAuto="false"
            :transitionTime="400"
          >
            <client-only>
              <div :class="$style.masonry">
                <div
                  v-for="(column, columnIndex) in reviewColumns"
                  :key="`col-${columnIndex}`"
                  :class="$style.masonryColumn"
                >
                  <HomeTestimonialsCard
                    v-for="review in column"
                    :key="review.id"
                    :review="review"
                    :fullText="getText(review)"
                    :translatedFrom="getTranslatedFrom(review)"
                    :recommendedLabel="$t('recommended')"
                    :readMoreLabel="$t('readMore')"
                    :viewOnFacebookLabel="$t('viewOnFacebook')"
                    :translatedFromLabel="
                      getTranslatedFrom(review)
                        ? $t(`translatedFrom.${getTranslatedFrom(review)}`)
                        : ''
                    "
                    @expand="onCardExpand"
                  />
                </div>
              </div>
              <template #placeholder>
                <HomeTestimonialsSkeleton />
              </template>
            </client-only>
          </Accordion>
        </div>

        <!--
          Fade must sit outside the clip (and Accordion’s will-change stacking
          context) or Safari paints clipped cards on top of the gradient and
          leaves a hairline at the bottom edge.
        -->
        <div v-if="!isFullyExpanded" :class="$style.fade" aria-hidden="true"></div>

        <div :class="$style.ctaStack">
          <button v-if="showSeeMore" type="button" :class="$style.seeMore" @click="onSeeMore">
            {{ $t('seeMore') }}
          </button>
          <a
            :class="$style.cta"
            :href="FACEBOOK_REVIEWS_URL"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconFacebookCircle :class="$style.ctaIcon" />
            <span :class="$style.ctaLabelDesktop">{{ $t('cta') }}</span>
            <span :class="$style.ctaLabelMobile">{{ $t('ctaShort') }}</span>
          </a>
        </div>
      </div>
    </TransitionOnScroll>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { shuffleArray } from '@/helpers/array'
import Accordion from '@/components/Accordion.vue'
import Heading, { HEADINGS } from '@/components/Heading/Heading.vue'
import IconFacebookCircle from '@/components/_icons/IconFacebookCircle.svg'
import TransitionOnScroll from '@/components/TransitionOnScroll.vue'
import HomeTestimonialsCard from './HomeTestimonialsCard.vue'
import HomeTestimonialsHighlights from './HomeTestimonialsHighlights.vue'
import HomeTestimonialsSkeleton from './HomeTestimonialsSkeleton.vue'
import { FACEBOOK_REVIEWS_URL, testimonials } from './testimonials'

/**
 * Render enough cards that expanding the clipped window (read more / See more)
 * still leaves content under the fade. Cards are partitioned into fixed columns
 * so expanding one review cannot rebalance / jump other cards.
 */
const RENDER_REVIEW_COUNT = testimonials.length

const COLLAPSED_HEIGHT = {
  // Mobile / tablet: taller so more cards show in the initial clip.
  default: 900,
  small: 960,
  large: 720,
  full: 680,
}

/** Extra height added on each “See more” click (larger on mobile/tablet). */
const SEE_MORE_DELTA = {
  default: 1200,
  small: 1200,
  large: 720,
  full: 680,
}

export default {
  name: 'HomeTestimonials',
  components: {
    Accordion,
    Heading,
    HomeTestimonialsCard,
    HomeTestimonialsHighlights,
    HomeTestimonialsSkeleton,
    IconFacebookCircle,
    TransitionOnScroll,
  },

  data() {
    return {
      FACEBOOK_REVIEWS_URL,
      HEADINGS,
      selectedReviews: [],
      hasGrownMasonry: false,
      isFullyExpanded: false,
      /** Clip height passed to Accordion; frozen after See more / Read more grows. */
      masonryClipHeight: COLLAPSED_HEIGHT.default,
    }
  },

  computed: {
    ...mapGetters({
      language: 'page/language',
      isMqSmall: 'viewport/isMqSmall',
      isMqLarge: 'viewport/isMqLarge',
      isMqFull: 'viewport/isMqFull',
    }),

    highlightItems() {
      return [
        { value: this.$t('highlight1Value'), label: this.$t('highlight1Label') },
        { value: this.$t('highlight2Value'), label: this.$t('highlight2Label') },
        { value: this.$t('highlight3Value'), label: this.$t('highlight3Label') },
      ]
    },

    collapsedMasonryHeight() {
      if (this.isMqFull) return COLLAPSED_HEIGHT.full
      if (this.isMqLarge) return COLLAPSED_HEIGHT.large
      if (this.isMqSmall) return COLLAPSED_HEIGHT.small
      return COLLAPSED_HEIGHT.default
    },

    seeMoreDelta() {
      if (this.isMqFull) return SEE_MORE_DELTA.full
      if (this.isMqLarge) return SEE_MORE_DELTA.large
      if (this.isMqSmall) return SEE_MORE_DELTA.small
      return SEE_MORE_DELTA.default
    },

    columnCount() {
      if (this.isMqFull) return 4
      if (this.isMqLarge) return 3
      if (this.isMqSmall) return 2
      return 1
    },

    // Round-robin into fixed columns so height changes stay local to one column.
    reviewColumns() {
      const count = this.columnCount
      const columns = Array.from({ length: count }, () => [])
      this.selectedReviews.forEach((review, index) => {
        columns[index % count].push(review)
      })
      return columns
    },

    showSeeMore() {
      return !this.isFullyExpanded
    },
  },

  watch: {
    // Accordion resets height when collapsedHeight changes while closed. After the
    // user has grown the clip, keep masonryClipHeight so breakpoint changes do not
    // snap back to the initial collapsed size.
    collapsedMasonryHeight: {
      immediate: true,
      handler(nextHeight) {
        if (!this.hasGrownMasonry) {
          this.masonryClipHeight = nextHeight
          return
        }
        this.$nextTick(() => {
          const { currentHeight, fullHeight } = this.getMasonryHeights()
          this.syncFullyExpanded(currentHeight, fullHeight)
        })
      },
    },
  },

  mounted() {
    // Keep shuffle in mounted (not created): SSG HTML is static, so reviews must be
    // chosen client-side after the client-only skeleton → avoid baking a fixed set
    // into the generated page while still showing a loading placeholder first.
    this.pickReviews()
  },

  methods: {
    pickReviews() {
      const pool = testimonials.slice()
      shuffleArray(pool)
      this.selectedReviews = pool.slice(0, Math.min(RENDER_REVIEW_COUNT, pool.length))
    },

    getText(review) {
      return (
        review.originalText[this.language] ||
        review.translatedText[this.language] ||
        Object.values(review.originalText)[0]
      )
    },

    getTranslatedFrom(review) {
      return !review.originalText[this.language] && review.translatedText[this.language]
        ? Object.keys(review.originalText)[0]
        : ''
    },

    getMasonryHeights() {
      const accordion = this.$refs.masonryAccordion
      const currentHeight = accordion?.$el?.offsetHeight || this.masonryClipHeight
      const fullHeight = accordion?.$el?.scrollHeight || currentHeight
      return { accordion, currentHeight, fullHeight }
    },

    syncFullyExpanded(currentHeight, fullHeight) {
      this.isFullyExpanded = currentHeight >= fullHeight - 4
    },

    growMasonryBy(delta) {
      const amount = Number(delta) || 0
      if (amount <= 0) return

      const { accordion, currentHeight, fullHeight } = this.getMasonryHeights()
      const nextHeight = Math.min(currentHeight + amount, fullHeight)

      this.hasGrownMasonry = true
      this.masonryClipHeight = nextHeight
      accordion?.animateToHeight?.(nextHeight)
      this.syncFullyExpanded(nextHeight, fullHeight)
    },

    onSeeMore() {
      // Reveal another chunk of the already-rendered masonry (taller on mobile/tablet).
      this.growMasonryBy(this.seeMoreDelta)
    },

    onCardExpand({ heightDelta, cardEl } = {}) {
      // Grow the clipped window by the card text delta only (not full open height).
      this.growMasonryBy(heightDelta)

      // Only nudge scroll if the expanded card left the viewport after growing.
      this.$nextTick(() => {
        if (!cardEl?.getBoundingClientRect) return
        const rect = cardEl.getBoundingClientRect()
        const margin = 24
        const fullyVisible = rect.top >= margin && rect.bottom <= window.innerHeight - margin
        if (!fullyVisible) {
          cardEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    },
  },
}
</script>

<style lang="scss" module>
.testimonialsContainer {
  background-color: $color-brand-sand-bg-light;
  padding: 68px 16px 72px;
  text-align: center;

  @include min-screen($mq-medium) {
    padding: 132px 24px 106px;
  }
}

.header {
  max-width: 640px;
  margin: 0 auto 40px;
}

.title {
  margin: 0 0 14px;
  color: $color-brand-navy;
}

.subtitle {
  margin: 0;
  font-size: 17px;
  font-weight: 300;
  color: $color-brand-navy;
  line-height: 1.55;
}

.masonryWrap {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
}

.masonryClip {
  position: relative;
  overflow: hidden;
  // Avoid transform/isolation here — they create a Safari compositing edge
  // (hairline) and fight Accordion’s will-change stacking.
  background-color: $color-brand-sand-bg-light;
}

.masonry {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  // Room for absolute CTAs when fully expanded (fade gone).
  padding-bottom: 76px;

  @include min-screen($mq-medium) {
    padding-bottom: 80px;
  }
}

.masonryColumn {
  flex: 1;
  min-width: 0;
}

.fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 220px;
  background: linear-gradient(
    to bottom,
    rgba($color-brand-sand-bg-light, 0) 0%,
    rgba($color-brand-sand-bg-light, 0.65) 35%,
    rgba($color-brand-sand-bg-light, 0.92) 65%,
    $color-brand-sand-bg-light 85%,
    $color-brand-sand-bg-light 100%
  );
  // Solid cap so the last device pixels never show clipped card edges.
  box-shadow: 0 2px 0 0 $color-brand-sand-bg-light;
  pointer-events: none;
  z-index: 1;
}

.ctaStack {
  position: absolute;
  // Full-width centering avoids Safari width bugs from left:50% + translateX(-50%).
  left: 0;
  right: 0;
  bottom: 8px;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  padding: 0 8px;
  box-sizing: border-box;
}

.seeMore {
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: rgba($color-white, 0.9);
  color: $color-brand-navy;
  border: 1.5px solid rgba($color-brand-navy, 0.28);
  font-weight: 600;
  font-size: 13px;
  line-height: 1;
  padding: 9px 16px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 4px 14px rgba($color-brand-navy, 0.08);
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease,
    transform 0.15s ease;

  @include min-screen($mq-medium) {
    font-size: 14px;
    padding: 10px 18px;
  }

  &:hover {
    background-color: rgba($color-white, 0.92);
    border-color: rgba($color-brand-navy, 0.45);
    transform: translateY(-1px);
  }
}

.cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: $color-brand-navy;
  color: $color-white;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  padding: 12px 18px;
  border-radius: 999px;
  // Softer shadow — the large navy blur read as a dark line under the pill on Safari.
  box-shadow: 0 4px 14px rgba($color-brand-navy, 0.18);
  // Size to the full label — never ellipsis or flex-shrink the text.
  white-space: nowrap;
  width: max-content;
  flex-shrink: 0;
  box-sizing: border-box;
  transition:
    background-color 0.15s ease,
    transform 0.15s ease;

  @include min-screen($mq-medium) {
    font-size: 15px;
    padding: 14px 22px;
  }

  &:hover {
    background-color: $color-brand-navy-dark-bg;
    transform: translateY(-1px);
    color: $color-white;
  }
}

.ctaIcon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

// Use the short label below desktop widths — iPhone landscape is often ≥450px and
// would otherwise get the long FR string with nowrap.
.ctaLabelDesktop {
  display: none;
  flex-shrink: 0;

  @include min-screen($mq-large) {
    display: inline;
  }
}

.ctaLabelMobile {
  display: inline;
  flex-shrink: 0;

  @include min-screen($mq-large) {
    display: none;
  }
}
</style>

<i18n src="@/locales/home-testimonials.json" />
